<template>
  <div class="edit-alumni-container" v-rect-loading="loading">
    <div class="header-container">
      {{ $t(`${i18nPrefix}.label.${ isEditMode ? 'editAlumni' : 'addAlumni'}`) }}
    </div>
    <div class="alumni-info">
      <div class="body-container">
        <div class="item-container email">
          <span class="label">{{ $t(`${i18nPrefix}.label.email`) }}:</span>
          <el-input class="content" v-model="alumni.email" :placeholder="$t(`${i18nPrefix}.placeholder.email`)" :disabled="isEditMode" @blur="validateEmail"></el-input>
        </div>
        <span class="error-tips email" v-show="alumni.emailError">{{ alumni.emailError }}</span>
        <div class="item-container phone">
          <span class="label">{{ $t(`${i18nPrefix}.label.phone`) }}:</span>
          <el-input class="content" v-model="alumni.phone" :placeholder="$t(`${i18nPrefix}.placeholder.phone`)" :disabled="isEditMode" @blur="validatePhone"></el-input>
        </div>
        <span class="error-tips phone" v-show="alumni.phoneError">{{ alumni.phoneError }}</span>
        <div class="item-container name">
          <span class="label">{{ $t(`${i18nPrefix}.label.name`) }}:</span>
          <el-input class="content" v-model="alumni.name" :placeholder="$t(`${i18nPrefix}.placeholder.name`)" @blur="validateName"></el-input>
        </div>
        <span class="error-tips name" v-show="alumni.nameError">{{ alumni.nameError }}</span>
        <div class="item-container birthday">
          <span class="label">{{ $t(`${i18nPrefix}.label.birthday`) }}:</span>
          <el-date-picker
            class="content"
            v-model="alumni.birthday"
            type="date"
            value-format="yyyy-MM-dd"
            :default-value="defaultDate"
            :picker-options="datePickerOptions"
            :placeholder="$t(`${i18nPrefix}.placeholder.birthday`)"
            >
          </el-date-picker>
        </div>
        <span class="error-tips common" v-show="alumni.commonError">{{ alumni.commonError }}</span>
      </div>
      <div class="btn-container">
        <el-button @click="handelCancel">{{ $t(`${i18nPrefix}.btn.cancelUpload`) }}</el-button>
        <el-button type="primary" @click="handelSave">{{ $t(`${i18nPrefix}.btn.ConfirmUpload`) }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
// import _ from 'lodash'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import { emailRegexp } from '@/utils/validator'

export default {
  components: {
  },
  data () {
    return {
      i18nPrefix: 'admin.company.alumniManagement',
      loading: false,
      alumni: {
        email: null,
        phone: null,
        name: null,
        birthday: null,
        emailError: null,
        phoneError: null,
        nameError: null,
        commonError: null
      },
      datePickerOptions: {
        disabledDate (time) {
          const defaultDate = moment().subtract(18, 'years')
          const currentDate = moment(time)
          return currentDate > defaultDate
        }
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    defaultDate () {
      return moment().subtract(18, 'years').format('YYYY-MM-DD')
    },
    alumniId () {
      return this.$route.params.alumniId
    }
  },
  created () {
    if (this.isEditMode && this.alumniId) {
      this.initData()
    }
  },
  methods: {
    ...mapActions('alumniManagement', ['getAlumni', 'createAlumni', 'updateAlumni']),
    async initData () {
      this.loading = true
      try {
        const resp = await this.getAlumni(this.alumniId)
        if (resp) {
          this.alumni.email = resp.email_address
          this.alumni.phone = resp.phone
          this.alumni.name = resp.name
          this.alumni.birthday = resp.birthday
        }
      } finally {
        this.loading = false
      }
    },
    validateEmail () {
      if (this.isEditMode) {
        return
      }
      const email = this.alumni.email
      if (email && !emailRegexp.test(email)) {
        this.alumni.emailError = this.$t('common.message.invalidEmail')
      } else {
        this.alumni.emailError = ''
      }
      if (email && this.alumni.commonError) {
        this.alumni.commonError = ''
      }
    },
    validatePhone () {
      if (this.isEditMode) {
        return
      }
      const phone = this.alumni.phone
      if (phone && !/^(\+)*(0)*(86)*1(\d){10}$/.test(phone)) {
        this.alumni.phoneError = this.$t('common.message.invalidMobile')
      } else {
        this.alumni.phoneError = ''
      }
      if (phone && this.alumni.commonError) {
        this.alumni.commonError = ''
      }
    },
    validateName () {
      if (this.alumni.name) {
        this.alumni.nameError = null
      } else {
        this.alumni.nameError = this.$t('common.message.requiredFormField')
      }
    },
    handelCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handelSave () {
      const alumni = this.alumni
      if (alumni.emailError || alumni.phoneError || alumni.nameError || alumni.commonError) {
        return
      }
      if (!this.isEditMode && !alumni.email && !alumni.phone) {
        alumni.commonError = this.$t(`${this.i18nPrefix}.message.missEmailOrPhone`)
        return
      }
      const saveMethod = this.isEditMode ? this.updateAlumni : this.createAlumni
      const params = {
        name: alumni.name,
        email_address: alumni.email,
        phone: alumni.phone,
        birthday: alumni.birthday
      }
      if (this.isEditMode) {
        params.id = this.alumniId
      }
      this.loading = true
      try {
        await saveMethod(params)
        this.handelCancel()
      } catch (err) {
        if (err.code === 20014001) {
          alumni.commonError = this.$t(`${this.i18nPrefix}.message.alumniIsExisted`)
        } else if (err.code === 20014002) {
          alumni.commonError = this.$t(`${this.i18nPrefix}.message.missEmailOrPhone`)
        } else if (err.code === 20014003) {
          alumni.nameError = this.$t('common.message.requiredFormField')
        } else {
          throw err
        }
      } finally {
        this.loading = false
      }
    }
  }
}

</script>

<style lang='scss' scoped>
.edit-alumni-container {
  .header-container {
    font-size: 18px;
    font-weight: 500;
    color: #303133;
    border-bottom: 1px solid #e4e7ed;
    padding-bottom: 12px;
  }

  .alumni-info {
    width: 606px;
    margin: 30px auto 0;
    .body-container {
      margin-top: 20px;

      .item-container {
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        .label {
          width: 15%;
          text-align: right;
          margin-right: 30px;
          display: inline-block;
        }

        .content {
          flex: 1;
          display: inline-block;

          ::v-deep {
            .el-input__inner {

              height: 32px;
              line-height: 32px;
              border-radius: 0;
            }
            .el-input__icon {
              line-height: 32px;
            }
          }
        }

      }
      .error-tips {
        padding-left: calc(15% + 30px);
        display: block;
        color: red;
      }
    }

    .btn-container {
      margin-top: 30px;
      display: flex;
      justify-content: flex-end;
    }
  }

}
</style>
